// ABOUTME: Centralized notification service for in-app and push notifications
// ABOUTME: Manages notification history, preferences, and delivery

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { type ExternalToast, toast } from 'sonner'

/**
 * Notification types supported by the app
 */
export type NotificationType =
  | 'achievement'
  | 'level_up'
  | 'challenge_complete'
  | 'streak'
  | 'quiz_complete'
  | 'new_content'
  | 'reminder'
  | 'system'

/**
 * Notification priority levels
 */
export type NotificationPriority = 'low' | 'medium' | 'high'

/**
 * Notification data structure
 */
export interface AppNotification {
  id: string
  type: NotificationType
  title: string
  message: string
  priority: NotificationPriority
  read: boolean
  createdAt: Date
  expiresAt?: Date
  action?: {
    label: string
    path: string
  }
  metadata?: Record<string, unknown>
}

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  enabled: boolean
  inAppEnabled: boolean
  quietHoursEnabled: boolean
  quietHoursStart: string
  quietHoursEnd: string
  types: Record<NotificationType, boolean>
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  enabled: true,
  inAppEnabled: true,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  types: {
    achievement: true,
    level_up: true,
    challenge_complete: true,
    streak: true,
    quiz_complete: true,
    new_content: true,
    reminder: true,
    system: true,
  },
}

interface NotificationStoreState {
  notifications: AppNotification[]
  preferences: NotificationPreferences
}

const STORAGE_KEY = 'gentleman-notifications'

function loadPersistedState(): NotificationStoreState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        notifications: parsed.notifications ?? [],
        preferences: parsed.preferences ?? DEFAULT_PREFERENCES,
      }
    }
  } catch {}
  return { notifications: [], preferences: DEFAULT_PREFERENCES }
}

function persistState(state: NotificationStoreState): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ notifications: state.notifications, preferences: state.preferences })
    )
  } catch {}
}

/**
 * Module-level state store for use outside React (in NotificationService class and push utilities)
 */
let _moduleState: NotificationStoreState = loadPersistedState()
type Listener = (state: NotificationStoreState) => void
const _listeners = new Set<Listener>()

function _setState(updater: (prev: NotificationStoreState) => NotificationStoreState): void {
  _moduleState = updater(_moduleState)
  persistState(_moduleState)
  for (const listener of _listeners) {
    listener(_moduleState)
  }
}

function _getState(): NotificationStoreState {
  return _moduleState
}

interface NotificationContextValue extends NotificationStoreState {
  addNotification: (notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) => string
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
  clearExpired: () => void
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void
  getUnreadCount: () => number
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function NotificationStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, setLocalState] = useState<NotificationStoreState>(() => _moduleState)
  const listenerRef = useRef<Listener>((s) => setLocalState({ ...s }))

  useEffect(() => {
    const listener = listenerRef.current
    _listeners.add(listener)
    return () => {
      _listeners.delete(listener)
    }
  }, [])

  const addNotification = useCallback(
    (notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>): string => {
      const id = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      const newNotification: AppNotification = {
        ...notification,
        id,
        createdAt: new Date(),
        read: false,
      }
      _setState((s) => ({
        ...s,
        notifications: [newNotification, ...s.notifications].slice(0, 100),
      }))
      return id
    },
    []
  )

  const markAsRead = useCallback((id: string) => {
    _setState((s) => ({
      ...s,
      notifications: s.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
    }))
  }, [])

  const markAllAsRead = useCallback(() => {
    _setState((s) => ({
      ...s,
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
    }))
  }, [])

  const removeNotification = useCallback((id: string) => {
    _setState((s) => ({
      ...s,
      notifications: s.notifications.filter((n) => n.id !== id),
    }))
  }, [])

  const clearAll = useCallback(() => {
    _setState((s) => ({ ...s, notifications: [] }))
  }, [])

  const clearExpired = useCallback(() => {
    const now = new Date()
    _setState((s) => ({
      ...s,
      notifications: s.notifications.filter((n) => !n.expiresAt || new Date(n.expiresAt) > now),
    }))
  }, [])

  const updatePreferences = useCallback((prefs: Partial<NotificationPreferences>) => {
    _setState((s) => ({ ...s, preferences: { ...s.preferences, ...prefs } }))
  }, [])

  const getUnreadCount = useCallback(
    () => state.notifications.filter((n) => !n.read).length,
    [state.notifications]
  )

  const value = useMemo<NotificationContextValue>(
    () => ({
      ...state,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      clearAll,
      clearExpired,
      updatePreferences,
      getUnreadCount,
    }),
    [
      state,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      clearAll,
      clearExpired,
      updatePreferences,
      getUnreadCount,
    ]
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export function useNotificationStore(): NotificationContextValue {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotificationStore must be used within NotificationStoreProvider')
  return ctx
}

/**
 * Notification service for sending and managing notifications
 */
export class NotificationService {
  private isInQuietHours(): boolean {
    const { preferences } = _getState()

    if (!preferences.quietHoursEnabled) {
      return false
    }

    const now = new Date()
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`

    const { quietHoursStart, quietHoursEnd } = preferences

    if (quietHoursStart > quietHoursEnd) {
      return currentTime >= quietHoursStart || currentTime < quietHoursEnd
    }

    return currentTime >= quietHoursStart && currentTime < quietHoursEnd
  }

  private isTypeEnabled(type: NotificationType): boolean {
    const { preferences } = _getState()
    return preferences.enabled && preferences.types[type]
  }

  private getToastOptions(type: NotificationType, priority: NotificationPriority): ExternalToast {
    const durationByPriority: Record<NotificationPriority, number> = {
      high: 8000,
      medium: 5000,
      low: 3000,
    }
    const baseDuration = durationByPriority[priority]

    const typeIcons: Record<NotificationType, string> = {
      achievement: '🏆',
      level_up: '⬆️',
      challenge_complete: '✅',
      streak: '🔥',
      quiz_complete: '📝',
      new_content: '🆕',
      reminder: '⏰',
      system: 'ℹ️',
    }

    return {
      duration: baseDuration,
      icon: typeIcons[type],
    }
  }

  notify(options: {
    type: NotificationType
    title: string
    message: string
    priority?: NotificationPriority
    action?: AppNotification['action']
    metadata?: Record<string, unknown>
    expiresAt?: Date
    showToast?: boolean
  }): string | null {
    const {
      type,
      title,
      message,
      priority = 'medium',
      action,
      metadata,
      expiresAt,
      showToast = true,
    } = options

    if (!this.isTypeEnabled(type)) {
      return null
    }

    const id = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newNotification: AppNotification = {
      type,
      title,
      message,
      priority,
      action,
      metadata,
      expiresAt,
      id,
      createdAt: new Date(),
      read: false,
    }
    _setState((s) => ({
      ...s,
      notifications: [newNotification, ...s.notifications].slice(0, 100),
    }))

    const { preferences } = _getState()
    if (showToast && preferences.inAppEnabled && !this.isInQuietHours()) {
      const toastOptions = this.getToastOptions(type, priority)

      if (priority === 'high') {
        toast.warning(title, {
          description: message,
          ...toastOptions,
        })
      } else {
        toast.success(title, {
          description: message,
          ...toastOptions,
        })
      }
    }

    return id
  }

  notifyAchievement(
    title: string,
    description: string,
    rarity: 'common' | 'rare' | 'epic' | 'legendary'
  ): string | null {
    const rarityEmoji: Record<string, string> = {
      common: '⭐',
      rare: '💫',
      epic: '✨',
      legendary: '🏆',
    }

    return this.notify({
      type: 'achievement',
      title: `${rarityEmoji[rarity]} Achievement Unlocked!`,
      message: `${title} - ${description}`,
      priority: {
        legendary: 'high' as const,
        epic: 'medium' as const,
        rare: 'low' as const,
        common: 'low' as const,
      }[rarity],
      metadata: { rarity },
    })
  }

  notifyLevelUp(newLevel: number, levelTitle: string): string | null {
    return this.notify({
      type: 'level_up',
      title: `Level ${newLevel} Reached!`,
      message: `Congratulations! You've become a ${levelTitle}`,
      priority: 'high',
      metadata: { level: newLevel, levelTitle },
    })
  }

  notifyChallenge(challengeName: string, xpEarned: number): string | null {
    return this.notify({
      type: 'challenge_complete',
      title: 'Challenge Completed!',
      message: `${challengeName} - +${xpEarned} XP`,
      priority: 'medium',
      metadata: { challengeName, xpEarned },
    })
  }

  notifyStreak(streakDays: number, isAboutToBreak = false): string | null {
    if (isAboutToBreak) {
      return this.notify({
        type: 'streak',
        title: 'Streak in Danger!',
        message: `Your ${streakDays} day streak is about to end. Complete a challenge to keep it going!`,
        priority: 'high',
        action: {
          label: 'Go to Challenges',
          path: '/challenges',
        },
      })
    }

    return this.notify({
      type: 'streak',
      title: `${streakDays} Day Streak!`,
      message: `You're on fire! Keep the momentum going.`,
      priority: streakDays >= 7 ? 'high' : 'medium',
      metadata: { streakDays },
    })
  }

  notifyQuizComplete(moduleName: string, score: number, isPerfect: boolean): string | null {
    return this.notify({
      type: 'quiz_complete',
      title: isPerfect ? 'Perfect Score!' : 'Quiz Completed!',
      message: `${moduleName} - Score: ${score}%`,
      priority: isPerfect ? 'high' : 'medium',
      metadata: { moduleName, score, isPerfect },
    })
  }

  notifyReminder(message: string): string | null {
    return this.notify({
      type: 'reminder',
      title: 'Daily Reminder',
      message,
      priority: 'low',
    })
  }

  notifySystem(
    title: string,
    message: string,
    priority: NotificationPriority = 'medium'
  ): string | null {
    return this.notify({
      type: 'system',
      title,
      message,
      priority,
    })
  }

  markAsRead(id: string): void {
    _setState((s) => ({
      ...s,
      notifications: s.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
    }))
  }

  markAllAsRead(): void {
    _setState((s) => ({
      ...s,
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
    }))
  }

  getUnreadCount(): number {
    return _getState().notifications.filter((n) => !n.read).length
  }

  getNotifications(): AppNotification[] {
    return _getState().notifications
  }

  clearExpired(): void {
    const now = new Date()
    _setState((s) => ({
      ...s,
      notifications: s.notifications.filter((n) => !n.expiresAt || new Date(n.expiresAt) > now),
    }))
  }

  updatePreferences(prefs: Partial<NotificationPreferences>): void {
    _setState((s) => ({ ...s, preferences: { ...s.preferences, ...prefs } }))
  }

  getPreferences(): NotificationPreferences {
    return _getState().preferences
  }
}

let serviceInstance: NotificationService | null = null

export function getNotificationService(): NotificationService {
  if (!serviceInstance) {
    serviceInstance = new NotificationService()
  }
  return serviceInstance
}

export function setupNotificationSystem(): void {
  const service = getNotificationService()
  service.clearExpired()
}
